import { useEffect, useState } from "react";
import { api, extractError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Tag as TagIcon, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { InfoTip } from "@/components/app/InfoTip";

const PRIORITIES = ["High", "Medium", "Low"];
const EMPTY = { cluster_name: "", category: "", subcategory: "", description: "", priority: "Medium", seed_keywords: "", negative_keywords: "" };

export default function Clusters() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [filter, setFilter] = useState("all");

  const load = () => api.get("/clusters").then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = {
        ...form,
        seed_keywords: typeof form.seed_keywords === "string" ? form.seed_keywords.split(",").map((s) => s.trim()).filter(Boolean) : form.seed_keywords,
        negative_keywords: typeof form.negative_keywords === "string" ? form.negative_keywords.split(",").map((s) => s.trim()).filter(Boolean) : form.negative_keywords,
      };
      if (editing) await api.put(`/clusters/${editing.id}`, payload);
      else await api.post("/clusters", payload);
      toast.success(editing ? "Cluster updated" : "Cluster created");
      setOpen(false); setEditing(null); setForm(EMPTY); load();
    } catch (e) { toast.error(extractError(e)); }
  };

  const onEdit = (c) => {
    setEditing(c);
    setForm({
      ...EMPTY, ...c,
      seed_keywords: (c.seed_keywords || []).join(", "),
      negative_keywords: (c.negative_keywords || []).join(", "),
    });
    setOpen(true);
  };

  const categories = ["all", ...Array.from(new Set(items.map((i) => i.category).filter(Boolean)))];
  const filtered = filter === "all" ? items : items.filter((i) => i.category === filter);
  const isAdmin = user?.role === "admin";

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400">Masters</p>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Keyword Clusters</h1>
          <p className="mt-1 text-sm text-slate-500">Configurable cluster library used to classify search terms during processing.</p>
        </div>
        {isAdmin && (
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(EMPTY); } }}>
            <DialogTrigger asChild>
              <Button data-testid="cluster-add-btn" className="bg-slate-900 text-white hover:bg-slate-800 rounded-lg"><Plus className="h-4 w-4 mr-1" /> New cluster</Button>
            </DialogTrigger>
            <DialogContent className="max-w-xl">
              <DialogHeader><DialogTitle>{editing ? "Edit cluster" : "Create cluster"}</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                <F label="Cluster name" required><Input data-testid="cluster-form-name" value={form.cluster_name} onChange={(e) => setForm({ ...form, cluster_name: e.target.value })} /></F>
                <div className="grid grid-cols-2 gap-3">
                  <F label="Category"><Input value={form.category || ""} onChange={(e) => setForm({ ...form, category: e.target.value })} /></F>
                  <F label="Subcategory"><Input value={form.subcategory || ""} onChange={(e) => setForm({ ...form, subcategory: e.target.value })} /></F>
                </div>
                <F label="Priority">
                  <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </F>
                <F label="Description"><Textarea value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} /></F>
                <F label="Seed keywords (comma-separated)" required><Textarea value={form.seed_keywords} onChange={(e) => setForm({ ...form, seed_keywords: e.target.value })} placeholder="vitamin c, glow, brightening" /></F>
                <F label="Negative keywords (comma-separated)"><Textarea value={form.negative_keywords} onChange={(e) => setForm({ ...form, negative_keywords: e.target.value })} placeholder="hair, body" /></F>
              </div>
              <DialogFooter><Button onClick={save} data-testid="cluster-form-save" className="bg-slate-900 text-white hover:bg-slate-800">{editing ? "Save" : "Create"}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {categories.map((c) => (
          <button
            key={c}
            data-testid={`cluster-filter-${c}`}
            onClick={() => setFilter(c)}
            className={`rounded-full border px-3 py-1 text-xs font-semibold tracking-wide ${
              filter === c ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
            }`}>{c === "all" ? "All categories" : c}</button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="clusters-list">
        {filtered.map((c) => (
          <div key={c.id} data-testid={`cluster-card-${c.id}`} className={`bg-white border rounded-xl p-5 shadow-sm hover:shadow-md transition-all ${c.auto_generated ? "border-violet-200 bg-gradient-to-br from-white to-violet-50/40" : "border-slate-200"}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2"><TagIcon strokeWidth={1.5} className="h-3.5 w-3.5 text-slate-400" /><span className="text-[11px] uppercase tracking-widest text-slate-500">{c.category || "—"}</span></div>
                <h3 className="mt-1 text-base font-semibold tracking-tight text-slate-900 inline-flex items-center gap-2">
                  {c.cluster_name}
                  {c.auto_generated && (
                    <span
                      data-testid={`cluster-auto-badge-${c.id}`}
                      title={c.seed_source ? `Auto-generated via ${c.seed_source}` : "Auto-generated"}
                      className="inline-flex items-center gap-0.5 rounded-full border border-violet-200 bg-violet-50 px-1.5 py-0 text-[10px] font-semibold uppercase tracking-wider text-violet-700"
                    >
                      <Sparkles className="h-2.5 w-2.5" />
                      AI
                      <InfoTip term="AUTO_CLUSTER" />
                    </span>
                  )}
                </h3>
              </div>
              {isAdmin && <Button variant="ghost" size="sm" onClick={() => onEdit(c)}><Pencil className="h-3.5 w-3.5" /></Button>}
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {(c.seed_keywords || []).slice(0, 8).map((s) => (
                <span key={s} className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] text-slate-600">{s}</span>
              ))}
              {(c.seed_keywords || []).length > 8 ? <span className="text-[11px] text-slate-400">+{c.seed_keywords.length - 8} more</span> : null}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function F({ label, required, children }) {
  return (
    <div>
      <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">
        {label}{required ? <span className="text-rose-500"> *</span> : null}
      </Label>
      {children}
    </div>
  );
}
